import { GA_MEASUREMENT_ID, GTM_CONTAINER_ID, USE_GTM } from '@/config/analytics';
import { sanitizeAnalyticsUrl } from '@/lib/analyticsUrl';
import type { CookiePreferences } from '@/lib/cookieConsent';

declare global {
  interface Window {
    dataLayer?: Record<string, unknown>[];
    gtag?: (...args: unknown[]) => void;
  }
}

let analyticsLoaded = false;

function initDataLayer() {
  window.dataLayer = window.dataLayer || [];
}

function loadGoogleTagManager() {
  if (!GTM_CONTAINER_ID) return;

  initDataLayer();
  window.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_CONTAINER_ID}`;
  document.head.appendChild(script);
}

function loadDirectGa4() {
  if (!GA_MEASUREMENT_ID) return;
  if (typeof window.gtag === 'function') return;

  initDataLayer();
  window.gtag = function gtag(...args: unknown[]) {
    window.dataLayer?.push(args as unknown as Record<string, unknown>);
  };
  window.gtag('js', new Date());
  window.gtag('config', GA_MEASUREMENT_ID, {
    page_location: sanitizeAnalyticsUrl(window.location.href),
  });

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);
}

/** GTM/GA4 yalnızca kullanıcı analitik veya pazarlama onayı verdiğinde yüklensin. */
export function shouldLoadGoogleAnalytics(preferences: CookiePreferences | null | undefined): boolean {
  if (!preferences) return false;
  return preferences.analytics || preferences.marketing;
}

/**
 * Analytics (GTM veya doğrudan GA4). Çağıran taraf önce shouldLoadGoogleAnalytics ile
 * onay kontrolü yapmalıdır; Consent Mode v2 güncellemesi src/lib/consentMode.ts içinde.
 */
export function loadGoogleAnalytics() {
  if (typeof window === 'undefined' || analyticsLoaded) return;
  if (USE_GTM && !GTM_CONTAINER_ID && !GA_MEASUREMENT_ID) return;
  if (!USE_GTM && !GA_MEASUREMENT_ID) return;

  analyticsLoaded = true;

  if (USE_GTM) {
    loadGoogleTagManager();
    return;
  }

  loadDirectGa4();
}

export function isGoogleAnalyticsLoaded() {
  return analyticsLoaded;
}

/** SPA route değişimlerinde GTM dataLayer veya doğrudan GA4 güncellemesi. */
export function pushAnalyticsPageView(path: string) {
  if (typeof window === 'undefined' || !analyticsLoaded) return;

  const safePath = sanitizeAnalyticsUrl(path);
  const pageLocation = `${window.location.origin}${safePath}`;

  initDataLayer();
  window.dataLayer?.push({
    event: 'page_view',
    page_path: safePath,
    page_location: pageLocation,
    page_title: document.title,
  });

  if (!USE_GTM && typeof window.gtag === 'function' && GA_MEASUREMENT_ID) {
    window.gtag('config', GA_MEASUREMENT_ID, { page_path: safePath, page_location: pageLocation });
  }
}

/** Özel olaylar — GTM Custom Event trigger ile yakalanır. */
export function pushAnalyticsEvent(eventName: string, params?: Record<string, unknown>) {
  if (typeof window === 'undefined' || !analyticsLoaded) return;

  initDataLayer();
  window.dataLayer?.push({
    event: eventName,
    ...params,
  });

  if (!USE_GTM && typeof window.gtag === 'function') {
    window.gtag('event', eventName, params ?? {});
  }
}
